import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, RefreshCw, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/contexts/LanguageContext';
import { CURRENCY_SYMBOLS } from '@/lib/currencyConversion';
import {
  type RecurringExpense,
  type RecurringFrequency,
  getCategoryIcon,
  getFrequencyShort,
  getDueDateLabel,
  getDueUrgencyClass,
  calculateMonthlyTotal,
} from '@/lib/recurringCalculations';
import { RecurringExpenseDialog } from './RecurringExpenseDialog';

export const RecurringExpensesManager = () => {
  const { toast } = useToast();
  const { t } = useLanguage();
  
  const [expenses, setExpenses] = useState<RecurringExpense[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currency, setCurrency] = useState('EUR');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<RecurringExpense | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingExpense, setDeletingExpense] = useState<RecurringExpense | null>(null);

  const loadExpenses = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get user's currency
      const { data: settings } = await supabase
        .from('budget_settings')
        .select('currency')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (settings?.currency) {
        setCurrency(settings.currency);
      }

      // Get recurring expenses
      const { data, error } = await supabase
        .from('recurring_expenses')
        .select('*')
        .eq('user_id', user.id)
        .order('next_due_date', { ascending: true });

      if (error) throw error;
      
      // Cast the data to ensure proper typing
      const typedData = (data || []).map(item => ({
        ...item,
        frequency: item.frequency as RecurringFrequency,
      }));
      
      setExpenses(typedData);
    } catch (error) {
      console.error('Load recurring expenses error:', error);
      toast({
        title: t('common.error'),
        description: 'Failed to load recurring expenses',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadExpenses();
  }, []);

  const handleDelete = async () => {
    if (!deletingExpense) return;

    try {
      const { error } = await supabase
        .from('recurring_expenses')
        .delete()
        .eq('id', deletingExpense.id);

      if (error) throw error;

      toast({ description: `"${deletingExpense.name}" removed` });
      loadExpenses();
    } catch (error) {
      console.error('Delete recurring expense error:', error);
      toast({
        title: t('common.error'),
        description: 'Failed to delete',
        variant: 'destructive',
      });
    } finally {
      setDeleteDialogOpen(false);
      setDeletingExpense(null);
    }
  };

  const handleEdit = (expense: RecurringExpense) => {
    setEditingExpense(expense);
    setDialogOpen(true);
  };

  const handleAddNew = () => {
    setEditingExpense(null);
    setDialogOpen(true);
  };

  const currencySymbol = CURRENCY_SYMBOLS[currency as keyof typeof CURRENCY_SYMBOLS] || currency;
  const monthlyTotal = calculateMonthlyTotal(expenses);
  const activeExpenses = expenses.filter(e => e.is_active);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <RefreshCw className="h-5 w-5 text-primary" />
          Recurring Expenses
        </h3>
        <Button size="sm" onClick={handleAddNew}>
          <Plus className="h-4 w-4 mr-1" />
          Add
        </Button>
      </div>

      {isLoading ? (
        <Card>
          <CardContent className="p-4">
            <div className="animate-pulse space-y-3">
              <div className="h-4 bg-muted rounded w-1/2" />
              <div className="h-4 bg-muted rounded w-3/4" />
            </div>
          </CardContent>
        </Card>
      ) : activeExpenses.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-6 text-center">
            <AlertCircle className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
            <p className="text-muted-foreground mb-3">
              No recurring expenses yet
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              Track subscriptions, bills, and regular payments to see your committed money.
            </p>
            <Button onClick={handleAddNew}>
              <Plus className="h-4 w-4 mr-2" />
              Add First Recurring Expense
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {activeExpenses.map((expense) => (
            <Card key={expense.id} className="overflow-hidden">
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <span className="text-xl flex-shrink-0">
                      {getCategoryIcon(expense.category)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium truncate">{expense.name}</span>
                        {expense.is_variable_amount && (
                          <Badge variant="outline" className="text-xs">~</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <span className={getDueUrgencyClass(expense.next_due_date)}>
                          {getDueDateLabel(expense.next_due_date)}
                        </span>
                        <span className="text-muted-foreground">·</span>
                        <span className="text-muted-foreground">
                          {expense.is_auto_paid ? 'Auto-paid' : 'Manual'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="text-right">
                      <span className="font-semibold">
                        {expense.is_variable_amount && '~'}
                        {currencySymbol}{expense.amount.toFixed(2)}
                      </span>
                      <span className="text-xs text-muted-foreground block">
                        {getFrequencyShort(expense.frequency)}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => handleEdit(expense)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => {
                        setDeletingExpense(expense);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Monthly Total */}
          <div className="pt-2 border-t mt-3">
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">Monthly total (approx.)</span>
              <span className="font-semibold">
                ~{currencySymbol}{monthlyTotal.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <RecurringExpenseDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        expense={editingExpense}
        onSaved={loadExpenses}
        currency={currency}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove recurring expense?</AlertDialogTitle>
            <AlertDialogDescription>
              Remove "{deletingExpense?.name}" from your recurring expenses?
              This won't affect past transactions.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
